import type { AggregationType, Field } from "@dto/fields";
import { LEFT, View } from "@dto/views";

/**
 * Pure transforms behind the "Organize fields" dialog of a view: the fields of
 * the object are listed in the order of the columns of the grid, the user
 * reorders them, shows or hides them and changes their aggregation in a draft,
 * and the view is saved once, when the draft is validated.
 */

// Side of the target field the dragged one is dropped on
export type DropPosition = 'before' | 'after';

// Why a field cannot be hidden from the dialog:
// - pivot: field of the pivot (its position places the pivot block, it stays movable)
// - pivotValue: value field of the pivot (displayed in the pivot block, under the pivot field)
// - horizontalGroup: last group of the view displayed on the left (always the first column)
export type FieldLock = 'pivot' | 'pivotValue' | 'horizontalGroup';

// One field of the object, with the key of its path (the key of the field
// itself when its path cannot be resolved: the field is then in error)
export interface FieldEntry {
    field: Field;
    pathKey: string;
    isError: boolean;
}

// What the user is editing: the order of the valid path keys (the fields in
// error have no place in the order of the view) and the shown path keys
export interface OrganizeFieldsDraft {
    order: string[];
    shown: string[];
    // Aggregations of the view by path key, which override the ones of the
    // fields (none: the aggregations of the view are not changed)
    aggregations?: Record<string, AggregationType>;
}

// One line of the dialog
export interface OrganizeFieldRow {
    pathKey: string;
    field: Field;
    visible: boolean;
    isError: boolean;
    lock: FieldLock | null;
    // Whether the field can be dragged (or moved with the keyboard)
    movable: boolean;
}

// Fields of the object with the keys of their paths (getPathKeys, aligned with
// the fields): a field whose path cannot be resolved is keyed by its own key,
// and is in error.
export function fieldEntries(fields: Field[], pathKeys: (string | null)[]): FieldEntry[] {
    return fields.map((field, index) => {
        const pathKey = pathKeys[index] ?? field._key;
        return { field, pathKey, isError: pathKey === field._key };
    });
}

// Fields which always stay shown, by path key. The pivot only applies when it
// has a pivot field; only the last group is displayed on the left. As for the
// grid, the groups on fields which are not valid any more (deleted...) do not count.
export function lockedPathKeys(view: View | null, entries: FieldEntry[]): Map<string, FieldLock> {
    const locks = new Map<string, FieldLock>();
    const validKeys = new Set(entries.filter(entry => !entry.isError).map(entry => entry.pathKey));
    const groups = view?.groups?.filter(group => validKeys.has(group.pathKey));
    const lastGroup = groups && groups.length > 0 ? groups[groups.length - 1] : null;
    if (lastGroup && lastGroup.displayType === LEFT) {
        locks.set(lastGroup.pathKey, 'horizontalGroup');
    }
    const pivot = view?.pivot;
    if (pivot?.pivotPathKey) {
        pivot.valuePathKeys?.forEach(pathKey => locks.set(pathKey, 'pivotValue'));
        locks.set(pivot.pivotPathKey, 'pivot');
    }
    return locks;
}

// Locked fields which keep their place in the order: they cannot be moved
export function pinnedPathKeys(locks: Map<string, FieldLock>): Set<string> {
    const pinned = new Set<string>();
    locks.forEach((lock, pathKey) => {
        if (lock !== 'pivot') {
            pinned.add(pathKey);
        }
    });
    return pinned;
}

// Order of the columns of the grid: the field order of the view (unknown and
// duplicated keys dropped), then the fields it misses, in the order of the
// object. Same rule as the server, which orders the columns it returns.
// The fields in error have no path: they have no place in the order of a view.
// They have one in the order of the object (withErrorFields), as any field.
export function orderPathKeys(entries: FieldEntry[], fieldOrder: string[] | null, withErrorFields: boolean = false): string[] {
    const validKeys = entries.filter(entry => withErrorFields || !entry.isError).map(entry => entry.pathKey);
    const valid = new Set(validKeys);
    const order: string[] = [];
    const seen = new Set<string>();
    for (const pathKey of fieldOrder ?? []) {
        if (valid.has(pathKey) && !seen.has(pathKey)) {
            order.push(pathKey);
            seen.add(pathKey);
        }
    }
    for (const pathKey of validKeys) {
        if (!seen.has(pathKey)) {
            order.push(pathKey);
            seen.add(pathKey);
        }
    }
    return order;
}

// Draft of the dialog when it opens. shownPathKeys are the path keys shown by
// the view (getShownPathKeys); the locked fields are shown whatever the view says,
// as the grid does. withErrorFields orders the fields in error with the other
// ones: the order of the fields of the object (configuration), not of a view.
export function initDraft(entries: FieldEntry[], view: View | null, shownPathKeys: string[], withErrorFields: boolean = false): OrganizeFieldsDraft {
    const locks = lockedPathKeys(view, entries);
    const shownSet = new Set(shownPathKeys);
    const shown = entries
        .filter(entry => shownSet.has(entry.pathKey) || (!entry.isError && locks.has(entry.pathKey)))
        .map(entry => entry.pathKey);
    return { order: orderPathKeys(entries, view?.fieldOrder ?? null, withErrorFields), shown, aggregations: viewAggregations(view) };
}

// Aggregations of the view by path key (a map once read from the server)
function viewAggregations(view: View | null): Record<string, AggregationType> {
    const aggregations = view?.aggregations;
    if (!aggregations) {
        return {};
    }
    return aggregations instanceof Map ? Object.fromEntries(aggregations) : { ...(aggregations as Record<string, AggregationType>) };
}

// Aggregation applied to a field in the draft: the one of the view, else the
// one of the field (null: none is set)
export function currentAggregation(draft: OrganizeFieldsDraft, entry: FieldEntry): AggregationType | null {
    return draft.aggregations?.[entry.pathKey] ?? entry.field.aggregation ?? null;
}

// Sets the aggregation of a field in the draft; null removes the one of the
// view (the field gets its own back). Choosing the aggregation the field
// already has changes nothing, as in the menu of the columns.
export function setAggregation(draft: OrganizeFieldsDraft, entry: FieldEntry, aggregation: AggregationType | null): OrganizeFieldsDraft {
    if (aggregation === currentAggregation(draft, entry)) {
        return draft;
    }
    const aggregations = { ...draft.aggregations };
    if (aggregation === null) {
        delete aggregations[entry.pathKey];
    } else {
        aggregations[entry.pathKey] = aggregation;
    }
    return { ...draft, aggregations };
}

// Whether the draft changes the aggregations of the view: the grid then has to
// drop its lines (their grouping changes) before they are fetched again
export function hasAggregationChanges(initial: OrganizeFieldsDraft, draft: OrganizeFieldsDraft): boolean {
    const before = initial.aggregations ?? {};
    const after = draft.aggregations ?? {};
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    return Array.from(keys).some(key => before[key] !== after[key]);
}

// Lines of the dialog, in the order of the columns of the grid: the horizontal
// group first (the grid always displays it first), the value fields of the
// pivot under the pivot field (the grid displays them in the pivot block, in
// the order of the pivot), and the fields in error at the end, unless the
// draft orders them (configuration of the object: they move as the others).
export function draftRows(entries: FieldEntry[], view: View | null, draft: OrganizeFieldsDraft): OrganizeFieldRow[] {
    const locks = lockedPathKeys(view, entries);
    const shown = new Set(draft.shown);
    const entryByKey = new Map(entries.map(entry => [entry.pathKey, entry]));
    const rows: OrganizeFieldRow[] = [];
    const added = new Set<string>();

    const addRow = (pathKey: string) => {
        const entry = entryByKey.get(pathKey);
        if (!entry || added.has(pathKey)) {
            return;
        }
        const lock = entry.isError ? null : locks.get(pathKey) ?? null;
        rows.push({
            pathKey,
            field: entry.field,
            visible: shown.has(pathKey),
            isError: entry.isError,
            lock,
            movable: lock === null || lock === 'pivot',
        });
        added.add(pathKey);
    };

    draft.order.filter(pathKey => locks.get(pathKey) === 'horizontalGroup').forEach(addRow);
    const pivot = view?.pivot;
    for (const pathKey of draft.order) {
        if (locks.has(pathKey) && locks.get(pathKey) !== 'pivot') {
            continue;
        }
        addRow(pathKey);
        if (pivot?.pivotPathKey === pathKey) {
            pivot.valuePathKeys?.forEach(addRow);
        }
    }
    // Locked fields not placed yet (none in practice)
    draft.order.forEach(addRow);

    // Fields in error the draft does not order (a view): listed at the end, they cannot move
    entries.filter(entry => entry.isError && !added.has(entry.pathKey)).forEach(entry => {
        rows.push({ pathKey: entry.pathKey, field: entry.field, visible: shown.has(entry.pathKey), isError: true, lock: null, movable: false });
    });
    return rows;
}

// Row next to which the drop line of a drag target is drawn. Dropped after the
// pivot field, a field lands after the value fields listed under it: the line
// is drawn under the last of them.
export function dropLineRow(rows: OrganizeFieldRow[], target: { key: string, position: DropPosition } | null): { key: string, position: DropPosition } | null {
    if (!target || target.position === 'before') {
        return target;
    }
    let index = rows.findIndex(row => row.pathKey === target.key);
    if (index === -1) {
        return target;
    }
    while (index + 1 < rows.length && rows[index + 1].lock === 'pivotValue') {
        index++;
    }
    return { key: rows[index].pathKey, position: 'after' };
}

// Whether the view changes the aggregation of a field: one of its aggregations,
// on a valid field, differs from the one of the field
export function hasAggregationOverrides(entries: FieldEntry[], view: View | null): boolean {
    const aggregations = viewAggregations(view);
    return entries.some(entry => !entry.isError
        && aggregations[entry.pathKey] !== undefined
        && aggregations[entry.pathKey] !== entry.field.aggregation);
}

// Number of fields the view hides, counted as the dialog shows them (locked
// fields and fields in error are never hidden)
export function countHiddenFields(entries: FieldEntry[], view: View | null, shownPathKeys: string[]): number {
    const draft = initDraft(entries, view, shownPathKeys);
    const shown = new Set(draft.shown);
    return draft.order.filter(pathKey => !shown.has(pathKey)).length;
}

// Returns the keys once the dragged one is dropped before or after the target
// one (never mutates its input), or null when the drop changes nothing:
// unknown key, dropped on itself or right where it already is.
export function moveKey(keys: string[], dragged: string, target: string, position: DropPosition): string[] | null {
    const draggedIndex = keys.indexOf(dragged);
    const targetIndex = keys.indexOf(target);
    if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
        return null;
    }
    const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
    if (insertIndex === draggedIndex || insertIndex === draggedIndex + 1) {
        return null;
    }
    const result = keys.filter(key => key !== dragged);
    // Removing the dragged key shifts the ones which follow it
    result.splice(insertIndex > draggedIndex ? insertIndex - 1 : insertIndex, 0, dragged);
    return result;
}

// Moves a field of the draft before or after another one. Only the fields which
// are not pinned move: the pinned ones keep their index in the order. Returns
// null when the move changes nothing or involves a pinned field.
export function moveFieldInDraft(draft: OrganizeFieldsDraft, dragged: string, target: string, position: DropPosition, pinned: ReadonlySet<string>): OrganizeFieldsDraft | null {
    if (pinned.has(dragged) || pinned.has(target)) {
        return null;
    }
    const movable = draft.order.filter(pathKey => !pinned.has(pathKey));
    const moved = moveKey(movable, dragged, target, position);
    if (!moved) {
        return null;
    }
    let index = 0;
    const order = draft.order.map(pathKey => pinned.has(pathKey) ? pathKey : moved[index++]);
    return { ...draft, order };
}

// Shows or hides a field. The locked fields and the fields in error (which
// have no place in the order) do not change.
export function toggleField(draft: OrganizeFieldsDraft, pathKey: string, locks: Map<string, FieldLock>): OrganizeFieldsDraft {
    if (!draft.order.includes(pathKey) || locks.has(pathKey)) {
        return draft;
    }
    const shown = draft.shown.includes(pathKey)
        ? draft.shown.filter(key => key !== pathKey)
        : [...draft.shown, pathKey];
    return { ...draft, shown };
}

// Shows every field (the fields in error do not change)
export function showAll(draft: OrganizeFieldsDraft): OrganizeFieldsDraft {
    const shown = new Set(draft.shown);
    return { ...draft, shown: [...draft.shown, ...draft.order.filter(pathKey => !shown.has(pathKey))] };
}

// Hides every field but the locked ones (the fields in error do not change)
export function hideAll(draft: OrganizeFieldsDraft, locks: Map<string, FieldLock>): OrganizeFieldsDraft {
    const order = new Set(draft.order);
    return { ...draft, shown: draft.shown.filter(pathKey => !order.has(pathKey) || locks.has(pathKey)) };
}

// Whether the draft shows at least one valid field. A view which shows none
// cannot be saved: the server drops an empty list of shown paths, and the view
// would show every field once reloaded.
export function hasVisibleField(draft: OrganizeFieldsDraft): boolean {
    const shown = new Set(draft.shown);
    return draft.order.some(pathKey => shown.has(pathKey));
}

function sameList(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((value, index) => value === b[index]);
}

function sameSet(a: string[], b: string[]): boolean {
    const setA = new Set(a);
    const setB = new Set(b);
    return setA.size === setB.size && Array.from(setA).every(value => setB.has(value));
}

// Whether the draft changes the order of the fields
export function hasOrderChanges(initial: OrganizeFieldsDraft, draft: OrganizeFieldsDraft): boolean {
    return !sameList(initial.order, draft.order);
}

// Keys of the fields in the order of the draft, to reorder the fields of the
// object: every field the draft orders, the fields in error included (they have
// an order of their own, even without a path). The server puts the fields it
// is not given (created meanwhile) after the listed ones.
export function draftFieldKeys(entries: FieldEntry[], draft: OrganizeFieldsDraft): string[] {
    const fieldKeyByPathKey = new Map(entries.map(entry => [entry.pathKey, entry.field._key]));
    return draft.order
        .map(pathKey => fieldKeyByPathKey.get(pathKey))
        .filter((key): key is string => key !== undefined);
}

// View to save once the draft is validated, or null when there is nothing to
// save. The whole view is sent (the server replaces it): only what changed is
// written, so that a view without its own field order keeps following the
// order of the object, and a legacy view keeps its hidden paths when only the
// order changes. A field order holding duplicates is rewritten: the server
// would display the duplicated columns twice.
export function buildViewPayload(view: View, initial: OrganizeFieldsDraft, draft: OrganizeFieldsDraft): View | null {
    const fieldOrder = view.fieldOrder ?? [];
    const orderChanged = !sameList(initial.order, draft.order) || new Set(fieldOrder).size !== fieldOrder.length;
    const visibilityChanged = !sameSet(initial.shown, draft.shown);
    const aggregationsChanged = hasAggregationChanges(initial, draft);
    if (!orderChanged && !visibilityChanged && !aggregationsChanged) {
        return null;
    }

    const payload: View = { ...view };
    if (orderChanged) {
        payload.fieldOrder = [...draft.order];
    }
    if (visibilityChanged) {
        const shown = new Set(draft.shown);
        const order = new Set(draft.order);
        payload.shownPaths = [
            ...draft.order.filter(pathKey => shown.has(pathKey)),
            // Fields in error the view showed
            ...draft.shown.filter(pathKey => !order.has(pathKey)),
        ];
        payload.hiddenPaths = null;
    }
    if (aggregationsChanged) {
        payload.aggregations = new Map(Object.entries(draft.aggregations ?? {}));
    }
    return payload;
}
